import React, { useRef, useState } from "react";
import { FieldError, UseFormRegister, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { Star } from "lucide-react";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";

interface FormInputProps {
    label: string;
    name: string;
    type?: string;
    register: ReturnType<UseFormRegister<any>>;
    error?: FieldError;
    className?: string;
    required?: boolean;
    setValue: UseFormSetValue<any>;
    watch: UseFormWatch<any>;
} 

const specialChars = ['µ', '%', '‰', '°', '²', '³', '⁺', '⁻', '₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];

const FormInputSpecialChar: React.FC<FormInputProps> = ({
    label,
    name,
    type = "text",
    register,
    error,
    className = "",
    required,
    setValue,
    watch
}) => {
    // Ref riêng để thao tác với input, không ghi đè ref của react-hook-form
    const localInputRef = useRef<HTMLInputElement | null>(null);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    // Hàm chèn ký tự đặc biệt vào vị trí con trỏ
    const insertSpecialChar = (char: string) => {
        const input = localInputRef.current;
        if (!input) return;
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const value = watch(name) || "";
        const newValue = value.slice(0, start) + char + value.slice(end);
        setValue(name, newValue, { shouldDirty: true });
        setTimeout(() => {
            input.setSelectionRange(start + char.length, start + char.length);
            input.focus();
        }, 0);
    };

    return (
        <div className={`${className}`}>
            <label className="block text-sm font-medium mb-1 text-gray-700" htmlFor={name}>{label} {required && <span className="text-red-500">*</span>}</label>
            <div className="relative flex gap-2 items-center">
                <input
                    id={name}
                    {...register}
                    ref={e => {
                        if (typeof register.ref === "function") register.ref(e);
                        localInputRef.current = e;
                    }}
                    type={type}
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                {/* Nút chọn ký tự đặc biệt */}
                <div className="absolute top-[5px] right-[2px] flex gap-2 items-center">
                    <Tooltip title="Ký tự đặc biệt" arrow placement="top">
                        <span>
                            <button
                                className="flex items-center justify-center w-8 h-8 rounded-full focus:outline-none"
                                aria-controls={open ? 'menu_specialChars' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                                onClick={handleClick}
                                type="button"
                            >
                                <Star />
                            </button>
                        </span>
                    </Tooltip>
                    <Menu
                        id="menu_specialChars"
                        className="specialChars"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                    >
                        {specialChars.map((char) => (
                            <button
                                type="button"
                                key={char}
                                className="px-2 py-1 border rounded bg-gray-100 hover:bg-gray-200 text-lg"
                                onClick={() => insertSpecialChar(char)}
                                tabIndex={-1}
                                title={`Chèn ký tự ${char}`}
                            >
                                {char}
                            </button>
                        ))}
                    </Menu>
                </div>
            </div>
            {error && (
                <span className="text-red-500 text-xs mt-1 block">{error.message as string}</span>
            )}
        </div>
    );
};

export default FormInputSpecialChar;
